if (!customElements.get("recently-viewed")) {
  customElements.define(
    "recently-viewed",
    class RecentlyViewed extends HTMLElement {
      observer = undefined;
      // Cache expiry time (5 minutes)
      static CACHE_DURATION = 5 * 60 * 1000;

      constructor() {
        super();
        this.productGrid = this.querySelector(".recently-viewed__slider");
      }

      connectedCallback() {
        this.recentlyViewed = localStorage.getItem("viewed-products")
          ? JSON.parse(localStorage.getItem("viewed-products"))
          : [];

        this.initializeRecentlyViewed(this.recentlyViewed);
        this.saveProductHandle();
      }

      saveProductHandle() {
        const productHandle = this.dataset.productHandle;

        if (this.recentlyViewed.includes(productHandle)) {
          this.recentlyViewed = this.recentlyViewed.filter(
            (handle) => handle !== productHandle
          );
        }

        this.recentlyViewed.unshift(productHandle);
        
        // Keep more in storage than we display for better UX
        if (this.recentlyViewed.length > 20) {
          this.recentlyViewed.pop();
        }
        
        localStorage.setItem(
          "viewed-products",
          JSON.stringify(this.recentlyViewed)
        );
      }

      initializeRecentlyViewed(recentlyViewed) {
        this.observer?.unobserve(this);
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadRecentlyViewed(recentlyViewed);
          },
          { rootMargin: "0px 0px 400px 0px" }
        );
        this.observer.observe(this);
      }

      /**
       * Get cached product from sessionStorage
       */
      static getCachedProduct(handle) {
        try {
          const cached = sessionStorage.getItem(`product-card-${handle}`);
          if (cached) {
            const { html, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < RecentlyViewed.CACHE_DURATION) {
              return html;
            }
            // Cache expired, remove it
            sessionStorage.removeItem(`product-card-${handle}`);
          }
        } catch (e) {
          console.error('Cache read error:', e);
        }
        return null;
      }

      /**
       * Save product to sessionStorage cache
       */
      static setCachedProduct(handle, html) {
        try {
          sessionStorage.setItem(
            `product-card-${handle}`,
            JSON.stringify({ html, timestamp: Date.now() })
          );
        } catch (e) {
          // Handle quota exceeded or other errors
          console.error('Cache write error:', e);
          // Optionally clear old cache entries if quota exceeded
          if (e.name === 'QuotaExceededError') {
            this.clearExpiredCache();
          }
        }
      }

      /**
       * Clear expired cache entries from sessionStorage
       */
      static clearExpiredCache() {
        try {
          const now = Date.now();
          const keysToRemove = [];
          
          // Find all product-card cache keys
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('product-card-')) {
              try {
                const cached = JSON.parse(sessionStorage.getItem(key));
                if (now - cached.timestamp >= RecentlyViewed.CACHE_DURATION) {
                  keysToRemove.push(key);
                }
              } catch (e) {
                // Invalid cache entry, mark for removal
                keysToRemove.push(key);
              }
            }
          }
          
          // Remove expired entries
          keysToRemove.forEach(key => sessionStorage.removeItem(key));
        } catch (e) {
          console.error('Cache cleanup error:', e);
        }
      }

      /**
       * Fetch product card HTML with sessionStorage caching
       */
      async fetchProductCard(productHandle) {
        // Try to get from cache first
        const cachedHTML = RecentlyViewed.getCachedProduct(productHandle);
        if (cachedHTML) {
          return cachedHTML;
        }

        // Not in cache, fetch from server
        try {
          const response = await fetch(
            `/products/${productHandle}?section_id=recently-viewed-card-product`
          );
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const text = await response.text();
          const html = new DOMParser().parseFromString(text, "text/html");
          const cardProduct = html.querySelector(".recently-viewed__product");
          
          if (cardProduct) {
            const cardHTML = cardProduct.outerHTML;
            // Cache the result
            RecentlyViewed.setCachedProduct(productHandle, cardHTML);
            return cardHTML;
          }
          
          return null;
        } catch (e) {
          console.error(`Failed to fetch product ${productHandle}:`, e);
          return null;
        }
      }

      async loadRecentlyViewed(recentlyViewed) {
        if (recentlyViewed.length === 0) return;

        const startTime = performance.now();
        let displayedCount = 0;
        const productsToDisplay = parseInt(this.dataset.productsToDisplay, 10) || 4;
        const currentProductHandle = this.dataset.productHandle;

        // Process products sequentially to maintain order and stop early if we hit the limit
        for (const productHandle of recentlyViewed) {
          // Stop if we've displayed enough products
          if (displayedCount >= productsToDisplay) break;
          
          // Skip current product
          if (productHandle === currentProductHandle) continue;

          const cardHTML = await this.fetchProductCard(productHandle);
          
          if (cardHTML) {
            this.productGrid.insertAdjacentHTML("beforeend", cardHTML);
            displayedCount++;

            // Show the component after first successful load
            if (displayedCount === 1) {
              this.style.visibility = "visible";
            }
          }
        }

        // Hide component if no products were loaded
        if (displayedCount === 0) {
          this.style.display = "none";
        }

        const endTime = performance.now();
        console.log(`Recently viewed loaded ${displayedCount} products in ${(endTime - startTime).toFixed(2)}ms`);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }
    }
  );
}